import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import UnderageNoticeDialog from '../shared/UnderageNoticeDialog';
import { isUnderVoterAge } from '../shared/voterAgeRules';
import NidCorrectionPreviewFrame from './NidCorrectionPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import NidCorrectionFormInfoBox from './NidCorrectionFormInfoBox';
import {
    cellKeys,
    CORRECTION_ROWS,
    countCompleteCorrections,
    displayField,
    formatDateField,
    getEmailValidationError,
    getEmptyNidCorrectionFields,
    getMobileValidationError,
    getNidCorrectionFormSnapshot,
    INITIAL_NID_CORRECTION_FORM,
    isNidCorrectionFormComplete,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    saveNidCorrectionFormSnapshot,
} from './nidCorrectionFormEngine';
import { downloadNidCorrectionPdf, printNidCorrectionForm } from './generateNidCorrectionPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';

/** Calendar range: old enough for any card holder's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

/** The one row of item ৩ whose two sides are dates rather than free text. */
const DATE_ROW_KEY = 'birthDate';

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, hint, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-1 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {hint ? (
                <p className="mb-3 text-[11.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{hint}</p>
            ) : <div className="mb-3.5" />}
            {children}
        </div>
    );
}

/** Plain field — no datalist here: the office data is one-off, not reusable. */
function FormInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass}`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function NidCorrectionFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    // Which blank the preview should be showing: the one being typed into.
    const [focusField, setFocusField] = useState('');
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_NID_CORRECTION_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Under-18 birth date on the ৪ নং row: warned about when picked, and again on
    // the way out — the corrected date is the one that lands on the card.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [contactErrors, setContactErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);
    /**
     * Which rows of item ৩ are open for editing. Nine rows × three cells is a
     * lot of empty boxes for an application that usually corrects one thing, so
     * a row only unfolds once it is asked for — and any row that already has
     * something in it (a restored snapshot) opens with the page.
     */
    const [openRows, setOpenRows] = useState(() => new Set());

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getNidCorrectionFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.holderName) || displayField(next.holderNid)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveNidCorrectionFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    /** Open every row that a restored snapshot already has something in. */
    const openFilledRows = useCallback((source) => {
        setOpenRows(new Set(
            CORRECTION_ROWS
                .filter((row) => {
                    const keys = cellKeys(row.key);
                    return displayField(source[keys.current])
                        || displayField(source[keys.corrected])
                        || displayField(source[keys.docs]);
                })
                .map((row) => row.key),
        ));
    }, []);

    const toggleRow = (rowKey) => {
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const handleDigitsChange = (key, raw, maxLength) => {
        update(key, normalizeDigits(raw, maxLength));
    };

    const handlePhoneChange = (key, raw) => {
        const digits = normalizeBdPhone(raw);
        update(key, digits);
        if (touched[key]) {
            setContactErrors((prev) => ({ ...prev, [key]: getMobileValidationError(digits, t) }));
        }
    };

    const validatePhone = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
        setContactErrors((prev) => ({ ...prev, [key]: getMobileValidationError(form[key], t) }));
    };

    const validateEmail = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
        setContactErrors((prev) => ({ ...prev, [key]: getEmailValidationError(form[key], t) }));
    };

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the form blank, or half-filled, to complete the rest
     * by hand at the office. Downloading one only asks for a confirmation that
     * names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveNidCorrectionFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyNidCorrectionFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('nidCorrectionForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/nid-information-correction-form', { category: 'general' });
            const base = form.holderName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadNidCorrectionPdf(previewRef.current, `nid-correction-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('nidCorrectionForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('nidCorrectionForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    // The corrected date is what the card will carry; without one, the date the
    // card carries today is the one worth checking.
    const birthDate = parseDateField(form[cellKeys(DATE_ROW_KEY).corrected])
        || parseDateField(form[cellKeys(DATE_ROW_KEY).current]);
    const underVoterAge = isUnderVoterAge(birthDate);

    const handleDownloadPdf = () => {
        if (underVoterAge) {
            setAgeNotice({ mode: 'download', date: birthDate });
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        // Printing is an export too — the same under-18 word of warning applies.
        if (underVoterAge) {
            setAgeNotice({ mode: 'print', date: birthDate });
            return;
        }
        await runPrint();
    };

    const runPrint = async () => {
        recordToolUsage('general/nid-information-correction-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('nidCorrectionForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printNidCorrectionForm(previewRef.current, t('nidCorrectionForm.errors.popupBlocked'));
            notifications.show({
                title: t('nidCorrectionForm.printTitle'),
                message: t('nidCorrectionForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('nidCorrectionForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_NID_CORRECTION_FORM);
        setContactErrors({});
        setTouched({});
        setFieldErrors({});
        setOpenRows(new Set());
        notifications.show({
            title: t('nidCorrectionForm.clearedTitle'),
            message: t('nidCorrectionForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getNidCorrectionFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('nidCorrectionForm.noSnapshotTitle'),
                message: t('nidCorrectionForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        openFilledRows(snapshot);
        setContactErrors({});
        setTouched({});
        setFieldErrors({});
        notifications.show({
            title: t('nidCorrectionForm.restoredTitle'),
            message: t('nidCorrectionForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.values(form).some((value) => displayField(value)),
        [form],
    );

    const isComplete = useMemo(() => isNidCorrectionFormComplete(form, t), [form, t]);
    const correctionCount = useMemo(() => countCompleteCorrections(form), [form]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('nidCorrectionForm.statusReady'),
        success: t('nidCorrectionForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`nidCorrectionForm.howTo.step${i}`)),
        [t],
    );

    const fieldError = (key) => {
        const err = contactErrors[key] || fieldErrors[key];
        if (!err) return undefined;
        return typeof err === 'string' && err.trim() ? err : ' ';
    };

    /** Every plain text field is wired the same way. */
    const textField = (key, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`nidCorrectionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                placeholder={t(`nidCorrectionForm.placeholders.${key}`)}
                error={fieldError(key)}
                {...extra}
            />
        </div>
    );

    const digitField = (key, maxLength, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`nidCorrectionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handleDigitsChange(key, v, maxLength)}
                placeholder={t(`nidCorrectionForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="numeric"
                maxLength={maxLength}
                {...extra}
            />
        </div>
    );

    const phoneField = (key) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`nidCorrectionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handlePhoneChange(key, v)}
                onBlur={() => validatePhone(key)}
                placeholder={t(`nidCorrectionForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="tel"
                type="tel"
                maxLength={11}
                autoComplete="tel"
            />
        </div>
    );

    const emailField = (key) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`nidCorrectionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                onBlur={() => validateEmail(key)}
                placeholder={t(`nidCorrectionForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="email"
                type="email"
                autoComplete="email"
            />
        </div>
    );

    /**
     * Dates are picked, not typed: the form wants দিন-মাস-বছর in Bengali
     * numerals, and a picker is the only way that shape comes out right every
     * time. A birth date can never be in the future — not the one on the card,
     * and not the corrected one either.
     */
    const dateCell = (key, placeholderKey) => (
        <DatePickerField
            value={parseDateField(form[key])}
            onChange={(date) => {
                update(key, formatDateField(date));
                if (isUnderVoterAge(date)) setAgeNotice({ mode: 'picked', date });
            }}
            formatValue={formatDateField}
            placeholder={t(`nidCorrectionForm.placeholders.${placeholderKey}`)}
            triggerClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
            fromYear={BIRTH_FROM_YEAR}
            toYear={TODAY.getFullYear()}
            disabled={{ after: TODAY }}
            clearable
        />
    );

    /** One subject of item ৩: what the card says, what it should say, the proof. */
    const correctionRow = (row) => {
        const keys = cellKeys(row.key);
        const isOpen = openRows.has(row.key);
        const rowName = t(`nidCorrectionForm.rows.${row.key}`);
        const isDate = row.key === DATE_ROW_KEY;
        const filled = displayField(form[keys.current]) || displayField(form[keys.corrected]);

        return (
            <div
                key={row.key}
                className={`rounded-xl border px-3 py-2.5 transition-colors ${
                    isOpen
                        ? 'border-purple-200 bg-purple-50/40 dark:border-purple-900/40 dark:bg-purple-950/10'
                        : 'border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900'
                }`}
            >
                <button
                    type="button"
                    onClick={() => toggleRow(row.key)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-2 text-left"
                >
                    <span className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        ({row.letter}) {rowName}
                    </span>
                    <span className="flex items-center gap-2">
                        {filled ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10.5px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                {t('nidCorrectionForm.rowFilled')}
                            </span>
                        ) : null}
                        <span className="text-[12px] font-semibold text-purple-600 dark:text-purple-400">
                            {isOpen ? t('nidCorrectionForm.rowHide') : t('nidCorrectionForm.rowEdit')}
                        </span>
                    </span>
                </button>

                {isOpen ? (
                    <div className="mt-3 space-y-3">
                        <div>
                            <FormLabel htmlFor={keys.current}>{t('nidCorrectionForm.cellCurrent')}</FormLabel>
                            {isDate ? dateCell(keys.current, 'cellCurrentDate') : (
                                <FormInput
                                    id={keys.current}
                                    name={keys.current}
                                    value={form[keys.current]}
                                    onChange={(v) => update(keys.current, v)}
                                    placeholder={t('nidCorrectionForm.placeholders.cellCurrent', { field: rowName })}
                                    error={fieldError(keys.current)}
                                />
                            )}
                        </div>
                        <div>
                            <FormLabel htmlFor={keys.corrected}>{t('nidCorrectionForm.cellCorrected')}</FormLabel>
                            {isDate ? dateCell(keys.corrected, 'cellCorrectedDate') : (
                                <FormInput
                                    id={keys.corrected}
                                    name={keys.corrected}
                                    value={form[keys.corrected]}
                                    onChange={(v) => update(keys.corrected, v)}
                                    placeholder={t('nidCorrectionForm.placeholders.cellCorrected', { field: rowName })}
                                    error={fieldError(keys.corrected)}
                                />
                            )}
                        </div>
                        <div>
                            <FormLabel htmlFor={keys.docs}>{t('nidCorrectionForm.cellDocs')}</FormLabel>
                            <FormInput
                                id={keys.docs}
                                name={keys.docs}
                                value={form[keys.docs]}
                                onChange={(v) => update(keys.docs, v)}
                                placeholder={t('nidCorrectionForm.placeholders.cellDocs')}
                                error={fieldError(keys.docs)}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <>
        <UnderageNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={ageNotice?.date ?? null}
            confirmLabel={ageNotice?.mode === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice?.mode === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice?.mode;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/nid-information-correction-form"
            subtitleKey="nidCorrectionForm.subtitle"
            // 30/70: the sheet is what the applicant is checking as they type, and
            // an A4 page needs the room to stay legible, so the fields take the
            // narrow column and the preview becomes the sticky rail beside them.
            // The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('nidCorrectionForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('nidCorrectionForm.clearAll')}
            primaryAction={{
                label: t('nidCorrectionForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Print and Restore ride in the sticky bar with Download and Clear,
            // so every action on the form is in one place at the foot of the
            // screen rather than parked beside a column you have scrolled past.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('nidCorrectionForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('nidCorrectionForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('nidCorrectionForm.sections.holder.number')}
                        title={t('nidCorrectionForm.sections.holder.title')}
                    >
                        <div className="space-y-3">
                            {textField('holderName', { autoComplete: 'name' })}
                            {digitField('holderNid', 17, { autoComplete: 'national-id' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('nidCorrectionForm.sections.guardian.number')}
                        title={t('nidCorrectionForm.sections.guardian.title')}
                        hint={t('nidCorrectionForm.sections.guardian.hint')}
                    >
                        <div className="space-y-3">
                            {textField('guardianName', { autoComplete: 'name' })}
                            {digitField('guardianNid', 17)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('nidCorrectionForm.sections.corrections.number')}
                        title={t('nidCorrectionForm.sections.corrections.title')}
                        hint={t('nidCorrectionForm.sections.corrections.hint')}
                    >
                        <div className="space-y-2">
                            {CORRECTION_ROWS.map(correctionRow)}
                        </div>
                        <p className={`mt-3 text-[11.5px] font-semibold ${
                            correctionCount
                                ? 'text-green-700 dark:text-green-400'
                                : 'text-orange-700 dark:text-orange-400'
                        }`}
                        >
                            {correctionCount
                                ? t('nidCorrectionForm.correctionCount', { count: correctionCount })
                                : t('nidCorrectionForm.correctionNone')}
                        </p>
                    </FormSection>

                    <FormSection
                        number={t('nidCorrectionForm.sections.fee.number')}
                        title={t('nidCorrectionForm.sections.fee.title')}
                    >
                        <div className="space-y-3">
                            {textField('feeAmount')}
                            {textField('feeReceipt')}
                            {textField('attachedDocs')}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('nidCorrectionForm.sections.applicantSign.number')}
                        title={t('nidCorrectionForm.sections.applicantSign.title')}
                    >
                        <div className="space-y-3">
                            {textField('applicantSignName', { autoComplete: 'name' })}
                            {textField('applicantSignAddress', { autoComplete: 'street-address' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {phoneField('applicantSignMobile')}
                                {emailField('applicantSignEmail')}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('nidCorrectionForm.sections.guardianSign.number')}
                        title={t('nidCorrectionForm.sections.guardianSign.title')}
                        hint={t('nidCorrectionForm.sections.guardianSign.hint')}
                    >
                        <div className="space-y-3">
                            {textField('guardianSignName', { autoComplete: 'name' })}
                            {textField('guardianSignAddress', { autoComplete: 'street-address' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {phoneField('guardianSignMobile')}
                                {emailField('guardianSignEmail')}
                            </div>
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('nidCorrectionForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('nidCorrectionForm.previewEmpty')}
                        idleHint={t('nidCorrectionForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the pages scroll inside it. It
                            takes the whole viewport bar the chrome above and the
                            action bar below, so the rail is not left half empty
                            beside a long form. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('nidCorrectionForm.livePreview')}
                            </div>
                            <NidCorrectionPreviewFrame
                                form={form}
                                previewRef={previewRef}
                                zoom={pageZoom.zoom}
                                focusField={focusField}
                            />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<NidCorrectionFormInfoBox />}
        />
        </>
    );
}
